import React, { useCallback, useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ImageIcon from '@mui/icons-material/Image';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import { deleteRequest, getRequest, postRequest, putRequest, showMessage } from '../../../config';
import AssistanceCardModal from './AssistanceCardModal';

const AssistanceCardsPage = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getRequest('/modules/assistance-cards');
            if (res?.code === 200 && res?.body?.items) setItems(res.body.items);
            else setItems([]);
        } catch (e) {
            console.error(e);
            showMessage('Could not load assistance cards.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const handleSubmit = async (data) => {
        try {
            const res = data._id
                ? await putRequest('/modules/assistance-cards', data)
                : await postRequest('/modules/assistance-cards', data);
            if (res?.code === 200) {
                showMessage(data._id ? 'Assistance card updated.' : 'Assistance card created.');
                setModalOpen(false);
                setEditing(null);
                load();
            } else {
                showMessage(res?.message || 'Save failed');
            }
        } catch (e) {
            console.error(e);
            showMessage('Save failed.');
        }
    };

    const handleDelete = async (row) => {
        if (!window.confirm(`Delete "${row.title}"?`)) return;
        const res = await deleteRequest(`/modules/assistance-cards/${row._id}`);
        if (res?.code === 200) {
            showMessage('Deleted.');
            load();
        }
    };

    return (
        <div className="modules-container">
            <div className="modules-header">
                <div>
                    <h1 className="modules-title">Assistance Cards</h1>
                    <p className="modules-subtitle">Manage helper cards with image/text variants</p>
                </div>
                <button type="button" onClick={() => { setEditing(null); setModalOpen(true); }} className="modules-create-btn df aic jcc cp">
                    <AddIcon style={{ fontSize: 18, marginRight: 8 }} />
                    Create Assistance Card
                </button>
            </div>

            <div className="modules-table-card">
                <div className="modules-table-header-row df jcsb aic mb1rem">
                    <h3 className="modules-table-title">Cards ({items.length})</h3>
                </div>

                {loading ? (
                    <div className="df jcc aic p4rem"><div className="loader" /><span className="ml1rem fs0-75rem c15">Loading...</span></div>
                ) : items.length ? (
                    <div className="modules-table-wrapper assistance-cards-table">
                        <table className="w100 fs0-75rem c15" style={{ borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid #e0e0e0', background: '#fafafa' }}>
                                    <th className='fs1rem tal' style={{ padding: '12px 16px' }}>Seq</th>
                                    <th className='fs1rem tal' style={{ padding: '12px 16px' }}>Type</th>
                                    <th className='fs1rem tal' style={{ padding: '12px 16px' }}>Title</th>
                                    <th className='fs1rem tal' style={{ padding: '12px 16px' }}>Description</th>
                                    <th className='fs1rem tal' style={{ padding: '12px 16px' }}>Button</th>
                                    <th className='fs1rem tal' style={{ padding: '12px 16px' }}>Status</th>
                                    <th className='fs1rem tal' style={{ padding: '12px 16px' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((row) => (
                                    <tr key={row._id} style={{ borderBottom: '1px solid #eee' }}>
                                        <td style={{ padding: '12px 16px', textAlign: 'left' }}>{row.sequence != null ? row.sequence : '—'}</td>
                                        <td style={{ padding: '12px 16px', textAlign: 'left' }}>
                                            <span className="df aic" style={{ gap: 6 }}>
                                                {row.type === 'Image' ? <ImageIcon style={{ fontSize: 16 }} /> : <TextFieldsIcon style={{ fontSize: 16 }} />} {row.type || 'Text'}
                                            </span>
                                        </td>
                                        <td style={{ padding: '12px 16px', textAlign: 'left' }}>{row.title || '—'}</td>
                                        <td style={{ padding: '12px 16px', textAlign: 'left', maxWidth: 280, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.description || row.topText || '—'}</td>
                                        <td style={{ padding: '12px 16px', textAlign: 'left' }}>{row.buttonText || '—'}</td>
                                        <td style={{ padding: '12px 16px', textAlign: 'left' }}>{row.isActive ? 'Active' : 'Inactive'}</td>
                                        <td className='df aic' style={{ padding: '12px 16px', textAlign: 'left' }}>
                                            <button type="button" onClick={() => { setEditing(row); setModalOpen(true); }} className="df aic jcc cp bg44 c12" style={{ padding: 6, marginRight: 8 }}>
                                                <EditIcon style={{ fontSize: 20 }} />
                                            </button>
                                            <button type="button" onClick={() => handleDelete(row)} className="df aic jcc cp bg44 c12" style={{ padding: 6 }}>
                                                <DeleteIcon style={{ fontSize: 20 }} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="df fdc aic jcc p4rem">
                        <p className="fs0-95rem c15 fw500 mb1rem">No assistance cards yet</p>
                        <button type="button" onClick={() => { setEditing(null); setModalOpen(true); }} className="modules-create-btn df aic jcc cp">
                            <AddIcon style={{ fontSize: 18, marginRight: 8 }} />Create Assistance Card
                        </button>
                    </div>
                )}
            </div>

            <AssistanceCardModal
                open={modalOpen}
                onClose={() => { setModalOpen(false); setEditing(null); }}
                onSubmit={handleSubmit}
                card={editing}
            />
        </div>
    );
};

export default AssistanceCardsPage;
